import { GridModel } from './grid.model';
import { Observable, catchError, forkJoin, map, of, throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { CrudService } from './crud.service';
import { ColumnDetails } from 'src/app/models/columnDetails.model';
import { GridExport } from 'src/app/models/gridExport.model';
import { chunkArray } from 'src/app/shared/utils/array.util';
import { GET_BY_IDS_BATCH_SIZE } from 'src/app/shared/constants/gridRequest.constants';

export interface IGridService<T> {
    get(filter: string, orderBy: string, sort: string, page: number, pageSize: number, filterBy: string, headers?: HttpHeaders): Observable<GridModel<T>>;
    getFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }>;
    export?(request: GridExport, headers?: HttpHeaders): Observable<HttpResponse<Blob>>;
}

export abstract class GridService<T> extends CrudService<T> implements IGridService<T> {

    constructor(controllerName: string,
        http: HttpClient) {
        super(controllerName, http);
    }

    get(filter: string, orderBy: string, sort: string, page: number, pageSize: number, filterBy: string, headers?: HttpHeaders): Observable<GridModel<T>> {
        return this.http.get<GridModel<T>>(this.getApiUrl(`${this.controllerName}/GetList`), {
            params: this.createParamsWithFilterBy(filter, orderBy, sort, page, pageSize, filterBy),
            headers: headers
        })
    }

    // GetList splits a comma-separated filterBy value into an IN query, so each batch is one request instead of one getById per id.
    getByIds(ids: string[]): Observable<T[]> {
        if (!ids.length) {
            return of([]);
        }
        const batches = chunkArray(ids, GET_BY_IDS_BATCH_SIZE).map(batch =>
            this.get('', 'id', 'asc', 0, batch.length, JSON.stringify({ id: batch.join(',') })).pipe(
                map(response => response.data ?? [])
            )
        );
        return forkJoin(batches).pipe(map(results => results.flat()));
    }

    getFieldConfiguration(): Observable<{ [property: string]: ColumnDetails }> {
        return this.http.get<{ [property: string]: ColumnDetails }>(
            this.getApiUrl(`${this.controllerName}/GetFieldConfiguration`)
        ).pipe(
            catchError((error) => {
                return throwError(() => error);
            })
        );
    }

    export(request: GridExport, headers?: HttpHeaders): Observable<HttpResponse<Blob>> {
        return this.http.post(
            this.getApiUrl(`${this.controllerName}/Export`),
            request,
            { responseType: 'blob', observe: 'response', headers }
        );
    }
}
